import React from 'react';
import { OrgOverviewMetricsSelector } from './OrgOverviewMetricsSelector';
import { useDispatch, useSelector } from 'react-redux';
import Form from 'antd/lib/form';
import { selectAIDisabledForOrgInsights, selectApp, selectOrgGroups, selectOrgUsers } from '@/selectors/selectors';
import { RootState } from '../../../reducers/reducers';
import { AppPlatform, OrgOverviewReleaseTypesFilter, StatisticCategory } from '@/types';
import { selectDestinationsData } from '@/selectors/summaryViewSelectors';
import { displayTextForAppPlatform } from '@/helpers/displayHelpers';
import { OrgOverviewTagInfo, renderTagSelectorSection } from './OrgOverviewTagSelector';
import { OrgMetricsDateRange, OrgNavigationDateRange, OrgNavigationDateRangeType } from './OrgNavigationDateRange';
import { useShouldShowNewOrgOverview, useShouldShowOrgOverviewInsights } from '@/utils/internalFeatureFlags';
import { Space, Switch } from 'antd/lib';
import Text from 'antd/lib/typography/Text';
import { BuildOutlined } from '@ant-design/icons';
import { runwayColors } from '@/components/styles/globalStyles';
import { tryFetchEntity } from '@/actions/entities';
import { FetchableEntityType } from '@/entityType';
import { useIsLoading } from '@/utils/hooks/helpers';
import Spin from 'antd/lib/spin';
import { OrgOverviewReleaseTypesSelector } from './OrgOverviewReleaseTypesSelector';
import { useGetOrgAvailableMetricsQuery } from '@/middlewares/defaultApi';
import { useOrgNavigationFilters } from './OrgNavigationFiltersContext';
import { GetAppNameWithPlatform } from '@/components/orgOverview/orgOverviewHelpers';

export interface OrgNavigationFilters {
    selectedDateRange: OrgMetricsDateRange;
    selectedDateRangeType: OrgNavigationDateRangeType;
    selectedMetrics: string[];
    selectedWorkflowApps: Record<string, string>;
    selectedDestinations: Record<string, string>;
    selectedPlatforms: Record<string, string>;
    selectedUsers: Record<string, string>;
    selectedReleaseTypes: OrgOverviewReleaseTypesFilter;
    shouldGenerateInsights: boolean;
}

interface OrgNavigationContentProps {
    orgId: string;
    isOrgSettingsRoute: boolean;
    filters: OrgNavigationFilters;
    setFilters: React.Dispatch<React.SetStateAction<OrgNavigationFilters>>;
}

export const OrgNavigationContent: React.FC<OrgNavigationContentProps> = ({
    orgId,
    isOrgSettingsRoute,
    filters,
    setFilters,
}) => {
    const { hasStoredFilters } = useOrgNavigationFilters();
    const shouldShowOrgOverview = useShouldShowNewOrgOverview();
    const shouldRender = orgId && !isOrgSettingsRoute && shouldShowOrgOverview;

    const dispatch = useDispatch();
    const { data: availableMetricsResponse, isLoading: isLoadingAvailableMetrics } = useGetOrgAvailableMetricsQuery(
        { orgId },
        { skip: !shouldRender },
    );
    const allowedStatisticCategories = React.useMemo(() => new Set(Object.values(StatisticCategory)), []);

    const availableMetrics = React.useMemo(
        () =>
            (availableMetricsResponse?.data?.metrics ?? []).filter(
                (metric) => !!metric.displayName && allowedStatisticCategories.has(metric.category),
            ),
        [availableMetricsResponse, allowedStatisticCategories],
    );

    const isAIDisabled = useSelector(
        (state: RootState) => selectAIDisabledForOrgInsights(state, orgId) || !useShouldShowOrgOverviewInsights(),
    );

    const orgDestinationsData = useSelector((state: RootState) => selectDestinationsData(state, orgId));
    const isLoadingOrgDestinations = useIsLoading(FetchableEntityType.OrgDestinationsAndVersions, orgId);
    const hasInitializedDefaultMetricsRef = React.useRef(hasStoredFilters || filters.selectedMetrics.length > 0);

    React.useEffect(() => {
        if (!orgDestinationsData && !isLoadingOrgDestinations) {
            dispatch(tryFetchEntity(FetchableEntityType.OrgDestinationsAndVersions, { orgId }, orgId));
        }
    }, [dispatch, orgId, orgDestinationsData, isLoadingOrgDestinations]);

    React.useEffect(() => {
        const availableMetricTypes = availableMetrics.map((metric) => metric.type);

        if (hasInitializedDefaultMetricsRef.current || !availableMetricTypes.length) {
            return;
        }

        hasInitializedDefaultMetricsRef.current = true;
        setFilters((currentFilters) => {
            if (currentFilters.selectedMetrics.length > 0) {
                return currentFilters;
            }

            return {
                ...currentFilters,
                selectedMetrics: availableMetricTypes,
            };
        });
    }, [availableMetrics, setFilters]);

    const [form] = Form.useForm<OrgNavigationFilters>();
    const submittedFiltersRef = React.useRef(filters);

    React.useEffect(() => {
        submittedFiltersRef.current = filters;
        form.setFieldsValue(filters);
    }, [filters, form]);

    const updateFilters = (partialFilters: Partial<OrgNavigationFilters>) => {
        const nextFilters = {
            ...filters,
            ...partialFilters,
        };

        submittedFiltersRef.current = nextFilters;
        form.setFieldsValue(nextFilters);
        form.submit();
    };

    const updateFilter = <K extends keyof OrgNavigationFilters>(key: K, value: OrgNavigationFilters[K]) => {
        updateFilters({ [key]: value });
    };

    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, orgId));
    const orgUsers = useSelector((state: RootState) => selectOrgUsers(state, orgId));

    const getUsersAndUserGroupsById = () => {
        const userGroups = Object.values(orgGroupsById).reduce(
            (acc, item) => {
                acc[item.id] = {
                    id: item.id,
                    name: item.name,
                    isTeam: true,
                };
                return acc;
            },
            {} as Record<string, OrgOverviewTagInfo>,
        );

        const users = orgUsers.reduce(
            (acc, item) => {
                acc[item.id] = {
                    id: item.id,
                    name: item.firstName ? `${item.firstName} ${item.lastName || ''}` : item.email,
                    imageUrl: item.commsAvatars?.image32 || item.commsAvatars?.image48,
                    appIds: item.appIds,
                    isUser: true,
                };
                return acc;
            },
            {} as Record<string, OrgOverviewTagInfo>,
        );
        return { ...userGroups, ...users };
    };

    const defaultAppsByType = {
        workflowAppsById: {} as Record<string, OrgOverviewTagInfo>,
        destinationsById: {} as Record<string, OrgOverviewTagInfo>,
    };
    const { workflowAppsById, destinationsById } =
        useSelector((state: RootState) => {
            return orgDestinationsData?.appDestinations.reduce((acc, { appId, appName, destinations }) => {
                const app = selectApp(state, appId);
                if (!appName || !app?.appSettings) {
                    return acc;
                }

                const isWorkflows = !!destinations?.length;

                if (isWorkflows) {
                    acc.workflowAppsById[appId] = {
                        id: appId,
                        name:
                            destinations.length === 1 ?
                                GetAppNameWithPlatform(appName, app.appSettings.platform)
                            :   appName,
                        imageUrl: app.appSettings.appImageUrl,
                        platform: app.appSettings.platform,
                        appId,
                    };

                    for (const destination of destinations) {
                        if (!destination.destinationId) {
                            continue;
                        }

                        const platform = (destination.platform as AppPlatform) || app.appSettings.platform;

                        acc.destinationsById[destination.destinationId] = {
                            id: destination.destinationId,
                            name: destination.name || GetAppNameWithPlatform(appName, platform),
                            imageUrl: destination.iconURL || app.appSettings.appImageUrl,
                            platform: platform,
                            appId: destination.appId,
                            isDestination: true,
                        };
                    }
                } else {
                    acc.destinationsById[appId] = {
                        id: appId,
                        name: GetAppNameWithPlatform(appName, app.appSettings.platform),
                        imageUrl: app.appSettings.appImageUrl,
                        platform: app.appSettings.platform,
                        appId,
                    };
                }

                return acc;
            }, defaultAppsByType);
        }) || defaultAppsByType;

    const getOrgPlatforms = (): Record<string, OrgOverviewTagInfo> => {
        return [workflowAppsById, destinationsById].reduce(
            (platformsById, itemsById) => {
                for (const item of Object.values(itemsById)) {
                    if (!item.platform || platformsById[item.platform]) {
                        continue;
                    }

                    platformsById[item.platform as string] = {
                        id: item.platform as string,
                        name: displayTextForAppPlatform(item.platform),
                    };
                }

                return platformsById;
            },
            {} as Record<string, OrgOverviewTagInfo>,
        );
    };

    const hasSelectedPlatforms = Object.keys(filters.selectedPlatforms).length > 0;

    const filterItemsBySelectedPlatforms = (
        itemsById: Record<string, OrgOverviewTagInfo>,
        selectedTags: Record<string, string>,
    ) => {
        return hasSelectedPlatforms ?
                Object.values(itemsById).reduce(
                    (acc, destination) => {
                        if (selectedTags[destination.id]) {
                            acc[destination.id] = destination;
                            return acc;
                        }

                        if (!destination.platform || !filters.selectedPlatforms[destination.platform]) {
                            return acc;
                        }

                        acc[destination.id] = destination;
                        return acc;
                    },
                    {} as Record<string, OrgOverviewTagInfo>,
                )
            :   itemsById;
    };

    const filteredWorkflowAppsById = filterItemsBySelectedPlatforms(workflowAppsById, filters.selectedWorkflowApps);
    const filteredDestinationsById = filterItemsBySelectedPlatforms(destinationsById, filters.selectedDestinations);

    const usersAndUserGroupsById = getUsersAndUserGroupsById();
    const selectedDestinationAppIds = Object.values(filters.selectedDestinations).reduce(
        (acc, destinationId) => {
            const destination = destinationsById[destinationId];
            if (destination?.appId) {
                acc[destination.appId] = destination.appId;
            }

            return acc;
        },
        {} as Record<string, string>,
    );
    const hasSelectedWorkflowApps = Object.keys(filters.selectedWorkflowApps).length > 0;
    const hasSelectedDestinations = Object.keys(filters.selectedDestinations).length > 0;
    const filteredUsersAndUserGroupsById =
        hasSelectedWorkflowApps || hasSelectedDestinations ?
            Object.values(usersAndUserGroupsById).reduce(
                (acc, item) => {
                    if (filters.selectedUsers[item.id]) {
                        acc[item.id] = item;
                        return acc;
                    }

                    if (!item.isUser) {
                        acc[item.id] = item;
                        return acc;
                    }

                    if (!item.appIds?.length) {
                        acc[item.id] = item;
                        return acc;
                    }

                    const matchesSelectedWorkflowApps =
                        !hasSelectedWorkflowApps || item.appIds.some((appId) => filters.selectedWorkflowApps[appId]);
                    const matchesSelectedDestinations =
                        !hasSelectedDestinations || item.appIds.some((appId) => selectedDestinationAppIds[appId]);

                    if (matchesSelectedWorkflowApps && matchesSelectedDestinations) {
                        acc[item.id] = item;
                    }

                    return acc;
                },
                {} as Record<string, OrgOverviewTagInfo>,
            )
        :   usersAndUserGroupsById;

    const shouldShowInitialLoadingState = !orgDestinationsData && isLoadingOrgDestinations;
    const selectedReleaseTypesValue = filters.selectedReleaseTypes;
    const setSelectedReleaseTypesValue = (value?: OrgOverviewReleaseTypesFilter) => {
        updateFilter('selectedReleaseTypes', value ?? OrgOverviewReleaseTypesFilter.AllReleaseTypes);
    };

    if (!shouldRender) {
        return null;
    }

    return (
        <Form form={form} component={false} onFinish={() => setFilters(submittedFiltersRef.current)}>
            {shouldShowInitialLoadingState ?
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '32px 16px',
                    }}
                >
                    <Spin size="large" />
                </div>
            :   <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignContent: 'flex-start',
                        padding: `0 16px 30px 16px`,
                    }}
                >
                    <OrgNavigationDateRange
                        selectedDateRange={filters.selectedDateRange}
                        setSelectedDateRange={(value) => updateFilters({ selectedDateRange: value })}
                        setSelectedDateRangeType={(value) => updateFilters({ selectedDateRangeType: value })}
                        setDateRange={(selectedDateRangeType, selectedDateRange) =>
                            updateFilters({
                                selectedDateRangeType,
                                selectedDateRange,
                            })
                        }
                        selectedDateRangeType={filters.selectedDateRangeType}
                    />

                    <OrgOverviewReleaseTypesSelector
                        selectedValue={selectedReleaseTypesValue}
                        onChange={setSelectedReleaseTypesValue}
                    />

                    <OrgOverviewMetricsSelector
                        availableMetrics={availableMetrics}
                        selectedMetrics={filters.selectedMetrics}
                        setSelectedMetrics={(value) => updateFilter('selectedMetrics', value)}
                        isLoading={isLoadingAvailableMetrics}
                    />

                    {!isAIDisabled ?
                        <Space style={{ paddingTop: 35 }}>
                            <Switch
                                size="small"
                                className="insights-switch"
                                checked={filters.shouldGenerateInsights}
                                onChange={(value) => updateFilter('shouldGenerateInsights', value)}
                            />
                            <BuildOutlined style={{ color: runwayColors.statusYellow }} />
                            <Text>Insights</Text>
                        </Space>
                    :   null}

                    {renderTagSelectorSection({
                        title: 'Flightpaths',
                        itemsById: filteredWorkflowAppsById,
                        selectedTags: filters.selectedWorkflowApps,
                        setSelectedTags: (value) => updateFilters({ selectedWorkflowApps: value }),
                        confirmSelectedTags: (value) => updateFilters({ selectedWorkflowApps: value }),
                    })}

                    {renderTagSelectorSection({
                        title: 'Destinations',
                        itemsById: filteredDestinationsById,
                        selectedTags: filters.selectedDestinations,
                        setSelectedTags: (value) => updateFilters({ selectedDestinations: value }),
                        confirmSelectedTags: (value) => updateFilters({ selectedDestinations: value }),
                    })}

                    {renderTagSelectorSection({
                        title: 'Platforms',
                        itemsById: getOrgPlatforms(),
                        selectedTags: filters.selectedPlatforms,
                        setSelectedTags: (value) => updateFilters({ selectedPlatforms: value }),
                        confirmSelectedTags: (value) => updateFilters({ selectedPlatforms: value }),
                    })}

                    {renderTagSelectorSection({
                        title: 'Teams / People',
                        itemsById: filteredUsersAndUserGroupsById,
                        selectedTags: filters.selectedUsers,
                        setSelectedTags: (value) => updateFilters({ selectedUsers: value }),
                        confirmSelectedTags: (value) => updateFilter('selectedUsers', value),
                    })}
                </div>
            }
        </Form>
    );
};
